import React, { useEffect, useState, useContext } from 'react';
import { useForm } from 'react-hook-form';
import './ManageClientInfo.redesign.scss';
import useFetch from 'use-http';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import { FormRow } from 'src/components/controls/forms/reactHookForm';
import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import { AnyObject } from 'final-form';
import Client from 'src/models/Client';
import ClientSettingsFormData from 'src/models/ClientSettingsFormData';
import DialogList from 'src/dialogs';
import DocumentManagement from 'src/components/layout/DocumentManagement';
import State from 'src/models/State';
import _ from 'lodash';

import LoadingSpinner from 'src/components/common/LoadingSpinner';
import Address from 'src/models/Address';
import PhoneNumber from 'src/models/PhoneNumber';
import PagedResultSet from 'src/models/common/PagedResultSet';

interface IManageClientInfoProps {
    states: PagedResultSet<State>;
    showDialog: (dialogName: string) => void;
    hideDialog: () => void;
}

const ManageClientInfo = (props: IManageClientInfoProps) => {
    const { register, handleSubmit, errors, reset } = useForm();
    const { get, response, put } = useFetch();
    const appContext = useContext<IAppContextState>(AppContext);

    const [formData, setFormData] = useState(null);
    const [clientData, setClientData] = useState<Client>(null);
    const [succesfulSubmit, setSuccesfulSubmit] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        let mount = true;

        if (mount) {
            getClientData();
        }

        return () => {
            mount = false;
        };
    }, []);

    useEffect(() => {
        getClientData();
    }, [succesfulSubmit]);

    useEffect(() => {
        if (!!clientData) {
            prepFormData();
        }
    }, [clientData]);

    useEffect(() => {
        if (!!formData) {
            reset(formData);
        }
    }, [formData]);

    const getClientData = async () => {
        setIsLoading(true);
        const client: Client = await get(`v1.0/Clients/${appContext.clientNumber}`);

        if (response.ok) {
            setClientData(client);
            setIsLoading(false);
        } else {
            console.log('Unable to get Clients Data.');
        }
    };

    const prepFormData = () => {
        let prepClient: Partial<Client> = {
            id: clientData ? clientData?.id : null,
            name: clientData ? clientData?.name : '',
            address: clientData?.address,
            phoneNumber: clientData ? clientData?.phoneNumber : null,
            isShadow: clientData ? clientData?.isShadow : false,
            cloakName: clientData ? clientData?.cloakName : '',
            calmsSourced: clientData ? clientData.calmsSourced : false,
            clientNumber: appContext.clientNumber ? appContext.clientNumber : 0,
            webSite: clientData ? clientData?.webSite : '',
        };

        let formatedData: AnyObject = {
            client: prepClient
        };

        setFormData(formatedData);
    };

    const filesUploded = () => {
        props.hideDialog();
    };

    const updateClientRecord = async (data: ClientSettingsFormData) => {
        const result = await put(`v1.0/Clients/${appContext.clientNumber}`, data.client);

        if (response.ok) {
            console.log('Client record has been updated ', result);
            setSuccesfulSubmit(true);
        }
    };

    const formatSubmitData = (values: ClientSettingsFormData) => {
        // "Sanitizing" Client Data
        let formatClientData: Client = {
            id: 0,
            name: '',
            address: new Address(),
            phoneNumber: new PhoneNumber(),
            isShadow: false,
            cloakName: '',
            calmsSourced: false,
            clientNumber: 0,
            webSite: '',
            isRealVisible: false,
            isFavorite: false
        };
        formatClientData.id = clientData.id ? parseInt(clientData.id + '', 10) : null;
        formatClientData.clientNumber = appContext.clientNumber ? appContext.clientNumber : 0;
        formatClientData.isShadow = !!clientData.isShadow ? clientData.isShadow : false;
        formatClientData.name = values.client.name ? values.client.name : '';
        formatClientData.cloakName = values.client.name ? values.client.name : '';
        formatClientData.calmsSourced = !!clientData.calmsSourced ? clientData.calmsSourced : false;
        formatClientData.address.id = clientData?.address?.id ? clientData?.address?.id : undefined;
        formatClientData.address.addressLine1 = values.client.address.addressLine1 ? values.client.address.addressLine1 : '';
        formatClientData.address.addressLine2 = values.client.address.addressLine2 ? values.client.address.addressLine2 : '';
        formatClientData.address.zipCode = values.client.address.zipCode ? values.client.address.zipCode : '';
        formatClientData.address.city = values.client.address.city ? values.client.address.city : '';
        formatClientData.address.state.id = values.client.address.state.id ? parseInt(values.client.address.state.id + '', 10) : null;
        formatClientData.address.stateId = values.client.address.state.id ? parseInt(values.client.address.state.id + '', 10) : null;
        formatClientData.phoneNumber = clientData.phoneNumber ? clientData.phoneNumber : null;
        formatClientData.webSite = clientData.webSite ? clientData.webSite : '';
        formatClientData.isRealVisible = !!clientData.isRealVisible ? clientData.isRealVisible : false;
        formatClientData.isFavorite = !!clientData.isFavorite ? clientData.isFavorite : false;

        let formatedData: AnyObject = {
            client: formatClientData
        };
        return formatedData as ClientSettingsFormData;
    };

    // Options for the state dropdown selection
    const stateDropdownOptions = () => {
        return props.states
            ? props.states?.items?.map((st: State) => {
                  return { value: st.id, label: st.name };
              })
            : [];
    };

    const onSubmit = (values: ClientSettingsFormData) => {
        const newValues = formatSubmitData(values);

        if (_.isEqual(newValues.client, formData.client)) {
            console.log('No change in Client Data');
        } else {
            updateClientRecord(newValues);
        }
    };

    return (
        <div>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div style={{ display: 'flex', marginBottom: '3rem' }}>
                    <div className="section-size">
                        <RHFLegend title="Client Information" />
                        {isLoading ? (
                            <LoadingSpinner />
                        ) : (
                            <FormRow style={{ display: 'flex', flexWrap: 'wrap', marginTop: '2.75rem' }}>
                                <div>
                                    <RHFInputBox
                                        name={'client.name'}
                                        label="Client Name:"
                                        labelStyle={{whiteSpace: 'nowrap', verticalAlign: 'middle'}}
                                        style={{width: '100%'}}
                                        bodyStyle={{width: '100%'}}
                                        register={register}
                                        errors={errors}
                                        rules={{ required: 'This field is required' }}
                                        type="text"
                                    />
                                </div>
                                <div>
                                    <RHFInputBox
                                        name={'client.address.addressLine1'}
                                        label="Address Line 1:"
                                        labelStyle={{whiteSpace: 'nowrap', verticalAlign: 'middle'}}
                                        style={{whiteSpace: 'nowrap', verticalAlign: 'middle'}}
                                        register={register}
                                        errors={errors}
                                        rules={{ required: 'This field is required' }}
                                        type="text"
                                    />
                                </div>
                                <div>
                                    <RHFInputBox
                                        name={'client.address.addressLine2'}
                                        label="Address Line 2:"
                                        labelStyle={{whiteSpace: 'nowrap', verticalAlign: 'middle'}}
                                        style={{whiteSpace: 'nowrap', verticalAlign: 'middle'}}
                                        register={register}
                                        errors={errors}
                                        type="text"
                                    />
                                </div>
                                {/* <div className="input-size">
                                    <RHFInputBox name="country" label="Country" register={register} errors={errors} type="text" />
                                </div> */}
                                <div className="input-size">
                                    <RHFInputBox
                                        name={'client.address.city'}
                                        label="City:"
                                        register={register}
                                        errors={errors}
                                        rules={{ required: 'This field is required' }}
                                        type="text"
                                    />
                                </div>
                                <div className="input-size">
                                    <RHFSelect
                                        label="State:"
                                        labelStyle={{whiteSpace: 'nowrap', verticalAlign: 'middle'}}
                                        style={{marginRight: '10px', whiteSpace: 'nowrap', verticalAlign: 'middle'}}
                                        name="client.address.state.id"
                                        options={stateDropdownOptions()}
                                        register={register}
                                        errors={errors}
                                    />
                                </div>
                                <div>
                                    <RHFInputBox
                                        name={'client.address.zipCode'}
                                        label={'Zip Code:'}
                                        labelStyle={{paddingLeft: '15px', whiteSpace: 'nowrap', verticalAlign: 'middle'}}
                                        style={{marginLeft: '3px', whiteSpace: 'nowrap', verticalAlign: 'middle'}}
                                        register={register}
                                        errors={errors}
                                        rules={{ required: 'This field is required' }}
                                        type="number"
                                    />
                                </div>
                            </FormRow>
                        )}
                    </div>
                </div>
                <div style={{ marginTop: '1rem', borderTop: '1px solid grey' }}>
                    <Button
                        className="blue-hover-button"
                        text="Attach Documents"
                        intent="secondary"
                        onClick={() => props.showDialog(DialogList.DOCUMENT_MANAGEMENT)}
                        style={{ marginTop: '1rem' }}
                    />
                </div>

                <div style={{ marginTop: '4rem' }}>
                    <Button text="Submit" intent="primary" type="submit" />
                </div>
            </form>
            <DocumentManagement filesUploaded={filesUploded} context="client settings" />
        </div>
    );
};

export default ManageClientInfo;


// WEBPACK FOOTER //
// ./src/components/project/sections/ManageClientInfo.redesign.tsx